import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { createOrder } from "../services/orderService";
import LocationPicker from "../Component/LocationPicker";
import Swal from 'sweetalert2';
import { calculateDistance, calculateDeliveryFee, sriLankaDistricts, getDistrictFromCoordinates } from "../utils/locationUtils";
import Headers from "../Component/Headers";
import Subheader from "../Component/Subheader";
import Footer from "../Component/Footer";
import { FaMoneyBillWave, FaCreditCard, FaUpload, FaCheckCircle, FaMapMarkerAlt, FaCrosshairs, FaExclamationTriangle, FaStore } from "react-icons/fa";
import "../styles/ProductPage.css";
import "../styles/Cart.css";
import "../styles/Checkout.css";

// Shop location coordinates (Maharagama)
const SHOP_LOCATION = {
  lat: 6.737862,
  lng: 79.903724
};

// Shop address details
const SHOP_ADDRESS = {
  name: "Main Store",
  address: "No.293/22, Suramya place, gorakapala, Panadura.",
  city: "Kalutara",
  phone: "+94 71 608 2005",
  hours: "9:00 AM - 6:00 PM (Monday - Saturday)"
};

function Checkout() {
  const navigate = useNavigate();
  const { cart, clearCart, getCartTotal } = useCart();

  const [billingInfo, setBillingInfo] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: ""
  });

  const [deliveryMethod, setDeliveryMethod] = useState("delivery"); // "delivery" or "pickup"
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [bankSlip, setBankSlip] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);

  // Location states
  const [userLocation, setUserLocation] = useState(null);
  const [deliveryDistance, setDeliveryDistance] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [locationMethod, setLocationMethod] = useState('');
  const [locationMessage, setLocationMessage] = useState({ type: '', text: '' });

  // Bank transfer details
  const bankDetails = {
    account_number: "+94 789621706",
    account_name: "Charith Silva",
    bank_name: "Commercial Bank",
    email: "chariths615@gmail.com"
  };

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/cart');
    }
  }, [cart.items.length, navigate]);

  // Calculate delivery fee when user location changes
  useEffect(() => {
    if (userLocation && deliveryMethod === "delivery") {
      const distance = calculateDistance(
        SHOP_LOCATION.lat,
        SHOP_LOCATION.lng,
        userLocation.lat,
        userLocation.lng
      );
      setDeliveryDistance(distance);
      setDeliveryFee(calculateDeliveryFee(distance));
    } else if (deliveryMethod === "pickup") {
      setDeliveryFee(0);
      setDeliveryDistance(0);
    }
  }, [userLocation, deliveryMethod]);

  // Reset location-related states when switching to pickup
  useEffect(() => {
    if (deliveryMethod === "pickup") {
      setUserLocation(null);
      setShowLocationPicker(false);
      setLocationMethod('');
      setLocationMessage({ type: '', text: '' });
    }
  }, [deliveryMethod]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingInfo((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSearchResultSelect = (searchResult) => {
    const newLocation = {
      lat: searchResult.lat,
      lng: searchResult.lng,
      method: 'search',
      address: searchResult.display_name
    };

    setUserLocation(newLocation);
    setLocationMethod('search');

    // Auto-fill address field if empty
    if (!billingInfo.address && searchResult.display_name) {
      setBillingInfo(prev => ({
        ...prev,
        address: searchResult.display_name
      }));
    }

    // Auto-fill city if available
    if (searchResult.address) {
      const city = searchResult.address.city ||
        searchResult.address.town ||
        searchResult.address.village ||
        searchResult.address.municipality;

      if (city && !billingInfo.city) {
        setBillingInfo(prev => ({
          ...prev,
          city: city
        }));
      }
    }
  };

  const getCurrentUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationMessage({
        type: 'error',
        text: 'Geolocation is not supported by this browser. Please select your location manually.'
      });
      return;
    }

    setGettingLocation(true);
    setLocationMessage({ type: 'info', text: 'Getting your location...' });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          method: 'gps'
        };

        setUserLocation(location);
        setLocationMethod('gps');
        setShowLocationPicker(true);
        setLocationMessage({ type: 'success', text: 'Location captured successfully!' });
        setGettingLocation(false);

        console.log('GPS Location obtained:', location);
      },
      (error) => {
        let errorMessage = 'Failed to get location. ';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please enable location permissions in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information unavailable. Please select location manually.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage += 'An unknown error occurred. Please select location manually.';
        }

        setLocationMessage({ type: 'error', text: errorMessage });
        setGettingLocation(false);

        // Show map with default location even if GPS fails
        setUserLocation({
          lat: 6.9271,
          lng: 79.8612,
          method: 'manual'
        });
        setLocationMethod('manual');
        setShowLocationPicker(true);
      },
      {
        timeout: 15000,
        enableHighAccuracy: true,
        maximumAge: 60000
      }
    );
  };

  const handleLocationSelect = (latlng) => {
    const newLocation = {
      lat: latlng.lat,
      lng: latlng.lng,
      method: 'manual'
    };
    setUserLocation(newLocation);
    setLocationMethod('manual');

    // Try to determine district for auto-fill
    const district = getDistrictFromCoordinates(latlng.lat, latlng.lng);
    if (district && !billingInfo.city) {
      setBillingInfo(prev => ({
        ...prev,
        city: district.name
      }));
    }
  };

  const handleDistrictSelect = (e) => {
    const selectedDistrict = e.target.value;
    if (selectedDistrict && sriLankaDistricts[selectedDistrict]) {
      const district = sriLankaDistricts[selectedDistrict];
      setUserLocation({
        ...district,
        method: 'district'
      });
      setLocationMethod('district');
      setShowLocationPicker(true);

      // Auto-fill city
      if (!billingInfo.city) {
        setBillingInfo(prev => ({
          ...prev,
          city: district.name
        }));
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, slip: 'Please upload a valid image (JPEG, PNG, JPG) or PDF file' }));
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, slip: 'File size should be less than 5MB' }));
        return;
      }

      setBankSlip(file);
      setErrors(prev => ({ ...prev, slip: '' }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setSlipPreview(e.target.result);
        reader.readAsDataURL(file);
      } else {
        setSlipPreview(null);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    const requiredFields = ['first_name', 'last_name', 'email', 'phone'];

    // For delivery, address and city are required
    if (deliveryMethod === "delivery") {
      requiredFields.push('address', 'city');
    }

    requiredFields.forEach(field => {
      if (!billingInfo[field].trim()) {
        newErrors[field] = `${field.replace('_', ' ')} is required`;
      }
    });

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (billingInfo.email && !emailRegex.test(billingInfo.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (Sri Lankan format)
    const phoneRegex = /^(?:\+94|0)?[1-9]\d{8}$/;
    if (billingInfo.phone && !phoneRegex.test(billingInfo.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid Sri Lankan phone number';
    }

    // Location validation for delivery
    if (deliveryMethod === "delivery" && !userLocation) {
      newErrors.location = 'Please select your delivery location';
    }

    // Bank transfer validation
    if (paymentMethod === "bank_transfer" && !bankSlip) {
      newErrors.slip = 'Please upload your payment slip';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) {
      alert('Please fix the errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        billing_info: {
          ...billingInfo,
          delivery_method: deliveryMethod
        },
        delivery_method: deliveryMethod,
        payment_method: paymentMethod,
        additional_notes: additionalNotes,
        items: cart.items.map(item => ({
          product_id: item.productId,
          variant_id: item.variantId,
          quantity: item.quantity,
          price: item.price
        })),
        subtotal: getCartTotal(),
        // tax: getCartTotal() * 0.1,
        tax: 0,
        delivery_fee: deliveryFee,
        // total: (getCartTotal() * 1.1) + deliveryFee
        total: getCartTotal() + deliveryFee
      };

      // Add location data only for delivery
      if (deliveryMethod === "delivery" && userLocation) {
        orderData.billing_info.latitude = userLocation.lat;
        orderData.billing_info.longitude = userLocation.lng;
        orderData.billing_info.delivery_distance = deliveryDistance;
        orderData.billing_info.delivery_fee = deliveryFee;
      }

      // Add pickup location for store pickup
      if (deliveryMethod === "pickup") {
        orderData.billing_info.pickup_location = SHOP_ADDRESS;
      }

      const orderResponse = await createOrder(orderData);

      // If bank transfer, store order ID for slip upload
      if (paymentMethod === "bank_transfer" && bankSlip) {
        localStorage.setItem('pendingOrder', JSON.stringify({
          orderId: orderResponse.data.id,
          slipFile: bankSlip
        }));
      }

      // Clear cart only after successful order creation
      clearCart();

      // Show SweetAlert success message instead of navigating
      await Swal.fire({
        title: 'Order Placed Successfully! 🎉',
        html: `
          <div style="text-align: left;">
            <div style="display: flex; align-items: center; margin-bottom: 15px;">
              <div style="background: #e8f5e8; padding: 10px; border-radius: 50%; margin-right: 10px;">
                <FaEnvelope style="color: #4caf50; font-size: 24px;" />
              </div>
              <div>
                <strong style="color: #2e7d32;">Check your email!</strong><br>
                <span style="color: #666; font-size: 14px;">We've sent the receipt to ${billingInfo.email}</span>
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span><strong>Order ID:</strong></span>
                <span style="color: #fd7e14;">#${orderResponse.data.id}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span><strong>Total Amount:</strong></span>
                <span style="color: #fd7e14; font-weight: bold;">Rs.${total.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span><strong>Payment Method:</strong></span>
                <span>${paymentMethod === 'cash' ? (deliveryMethod === 'pickup' ? 'Cash on Pickup' : 'Cash on Delivery') : 'Bank Transfer'}</span>
              </div>
            </div>

            ${deliveryMethod === 'delivery' ? `
              <div style="background: #e3f2fd; padding: 12px; border-radius: 8px; margin: 10px 0;">
                <strong>📦 Delivery Information</strong><br>
                <small>We'll deliver to: ${billingInfo.address}, ${billingInfo.city}</small><br>
                <small>Estimated delivery: 2-3 business days</small>
              </div>
            ` : `
              <div style="background: #e8f5e8; padding: 12px; border-radius: 8px; margin: 10px 0;">
                <strong>🏪 Pickup Information</strong><br>
                <small>Pickup from: ${SHOP_ADDRESS.address}, ${SHOP_ADDRESS.city}</small><br>
                <small>We'll notify you when ready for pickup</small>
              </div>
            `}

            ${paymentMethod === 'bank_transfer' ? `
              <div style="background: #fff3cd; padding: 12px; border-radius: 8px; margin: 10px 0;">
                <strong>🏦 Payment Verification</strong><br>
                <small>Your order will be processed after payment verification</small>
              </div>
            ` : ''}
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Continue Shopping',
        confirmButtonColor: '#FD7E14',
        showCancelButton: true,
        cancelButtonText: 'View Order Details',
        customClass: {
          popup: 'custom-swal-popup'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          // Navigate to home page
          navigate('/');
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          // Navigate to order details page (you might need to create this)
          navigate('/orders');
        }
      });

    } catch (error) {
      console.error("Order creation failed:", error);

      // Show error SweetAlert
      await Swal.fire({
        title: 'Order Failed!',
        text: 'Failed to place order. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  // const subTotal = getCartTotal();
  // // const tax = subTotal * 0.1;
  // const tax = 0;
  // const total = subTotal + tax + deliveryFee;

  const subTotal = getCartTotal();
  const tax = 0;
  const total = subTotal + deliveryFee;

  return (
    <div>
      <Subheader />

      <div className="container py-5">
        <p className="mb-5 routepath_title"><Link to="/" className="breadcrumb-link">Home</Link> / Checkout</p>
        <div className="row">
          {/* LEFT COLUMN */}
          <div className="col-md-7 mb-4">
            <p className="checkout_title">Billing Information</p>
            <form>
              {/* Basic Billing Fields */}
              <div className="row">
                <div className="col-6 mb-3">
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                    name="first_name"
                    value={billingInfo.first_name}
                    onChange={handleInputChange}
                  />
                  {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
                </div>
                <div className="col-6 mb-3">
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                    name="last_name"
                    value={billingInfo.last_name}
                    onChange={handleInputChange}
                  />
                  {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  name="email"
                  value={billingInfo.email}
                  onChange={handleInputChange}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label">Phone *</label>
                <input
                  type="text"
                  className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                  name="phone"
                  value={billingInfo.phone}
                  onChange={handleInputChange}
                  placeholder="+94 77 123 4567"
                />
                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
              </div>

              {/* Delivery Method Selection */}
              <p className="mt-4 checkout_title">Delivery Method</p>

              <div className="row mb-4">
                <div className="col-6">
                  <div
                    className={`delivery-option d-flex flex-column align-items-center p-3 border rounded ${deliveryMethod === "delivery" ? "selected" : ""}`}
                    onClick={() => setDeliveryMethod("delivery")}
                  >
                    <FaMapMarkerAlt size={40} className="mb-2 text-primary" />
                    <span className="fw-semibold">Home Delivery</span>
                    <small className="text-muted text-center mt-1">We deliver to your location</small>
                    {deliveryFee > 0 && (
                      <small className="text-primary fw-bold mt-1">Rs.{deliveryFee.toFixed(2)}</small>
                    )}
                  </div>
                </div>

                <div className="col-6">
                  <div
                    className={`delivery-option d-flex flex-column align-items-center p-3 border rounded ${deliveryMethod === "pickup" ? "selected" : ""}`}
                    onClick={() => setDeliveryMethod("pickup")}
                  >
                    <FaStore size={40} className="mb-2 text-success" />
                    <span className="fw-semibold">Store Pickup</span>
                    <small className="text-muted text-center mt-1">Pick up from our store</small>
                    <small className="text-success fw-bold mt-1">FREE</small>
                  </div>
                </div>
              </div>

              {/* Address Section - Only show for delivery */}
              {deliveryMethod === "delivery" && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Address *</label>
                    <textarea
                      className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                      name="address"
                      rows="2"
                      value={billingInfo.address}
                      onChange={handleInputChange}
                      placeholder="Enter your complete address including street, area, and landmarks"
                    ></textarea>
                    {errors.address && <div className="invalid-feedback">{errors.address}</div>}
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">City *</label>
                      <input
                        type="text"
                        className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                        name="city"
                        value={billingInfo.city}
                        onChange={handleInputChange}
                        placeholder="Colombo"
                      />
                      {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Postal Code</label>
                      <input
                        type="text"
                        className="form-control"
                        name="postal_code"
                        value={billingInfo.postal_code}
                        onChange={handleInputChange}
                        placeholder="10000"
                      />
                    </div>
                  </div>

                  {/* Delivery Location Section */}
                  <div className="mb-4">
                    <p className="checkout_title">Delivery Location</p>

                    <div className="card">
                      <div className="card-body">
                        {/* Location Method Info */}
                        {locationMethod && (
                          <div className={`alert ${locationMethod === 'gps' ? 'alert-success' : 'alert-info'} mb-3`}>
                            <small>
                              <FaMapMarkerAlt className="me-1" />
                              <strong>Location set via:</strong> {
                                locationMethod === 'gps' ? 'GPS Location' :
                                  locationMethod === 'district' ? 'District Selection' :
                                    'Map Selection'
                              }
                            </small>
                          </div>
                        )}

                        {/* Location Message */}
                        {locationMessage.text && (
                          <div className={`alert ${locationMessage.type === 'error' ? 'alert-danger' : locationMessage.type === 'success' ? 'alert-success' : 'alert-info'} mb-3`}>
                            <small>
                              {locationMessage.type === 'error' && <FaExclamationTriangle className="me-1" />}
                              {locationMessage.type === 'success' && <FaCheckCircle className="me-1" />}
                              {locationMessage.text}
                            </small>
                          </div>
                        )}

                        {/* Location Actions */}
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <button
                              type="button"
                              className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                              onClick={getCurrentUserLocation}
                              disabled={gettingLocation}
                            >
                              {gettingLocation ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Getting Location...
                                </>
                              ) : (
                                <>
                                  <FaCrosshairs className="me-2" />
                                  Use GPS Location
                                </>
                              )}
                            </button>
                            <small className="text-muted d-block mt-1">
                              Click to detect your current location
                            </small>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Or Select District</label>
                            <select
                              className="form-select"
                              onChange={handleDistrictSelect}
                              defaultValue=""
                            >
                              <option value="">Choose your district...</option>
                              {Object.entries(sriLankaDistricts).map(([key, district]) => (
                                <option key={key} value={key}>
                                  {district.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Location Display */}
                        {userLocation && (
                          <div className="location-info alert alert-success">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <FaMapMarkerAlt className="me-2" />
                                <strong>Delivery Location Set</strong>
                                <br />
                                <small>
                                  Coordinates: {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
                                </small>
                                <br />
                                <small>
                                  <strong>Distance:</strong> {deliveryDistance.toFixed(2)} km from shop •
                                  <strong> Delivery Fee:</strong> Rs.{deliveryFee.toFixed(2)}
                                </small>
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setShowLocationPicker(!showLocationPicker)}
                              >
                                {showLocationPicker ? 'Hide Map' : 'Adjust on Map'}
                              </button>
                            </div>
                          </div>
                        )}

                        {errors.location && (
                          <div className="alert alert-danger">
                            <FaExclamationTriangle className="me-1" />
                            {errors.location}
                          </div>
                        )}

                        {/* Interactive Map */}
                        {showLocationPicker && (
                          <div className="mt-3">
                            <div className="mb-2">
                              <small className="text-muted">
                                💡 <strong>Click anywhere on the map</strong> to set your exact delivery location
                              </small>
                            </div>
                            <LocationPicker
                              onLocationSelect={handleLocationSelect}
                              userLocation={userLocation}
                              shopLocation={SHOP_LOCATION}
                            />
                            <div className="text-center mt-2">
                              <small className="text-muted">
                                🔵 Your location • 🏪 Our shop (Moratuwa)
                              </small>
                            </div>
                          </div>
                        )}

                        {/* Location Help Text */}
                        {!userLocation && (
                          <div className="alert alert-light border mt-3">
                            <h6 className="mb-2">📍 Delivery Location Required</h6>
                            <small className="text-muted mb-0">
                              We calculate delivery fees based on distance from our shop in Moratuwa.
                              Delivery fee is Rs.100 base + Rs.50 per km.
                              Use GPS for automatic detection or select your location manually on the map.
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Store Pickup Information */}
              {deliveryMethod === "pickup" && (
                <div className="mb-4">
                  <p className="checkout_title">Store Pickup Information</p>
                  <div className="card border-success">
                    <div className="card-body">
                      <div className="d-flex align-items-start">
                        <FaStore className="text-success me-3 mt-1" size={24} />
                        <div>
                          <h6 className="card-title text-success mb-2">{SHOP_ADDRESS.name}</h6>
                          <p className="card-text mb-1">
                            <strong>Address:</strong> {SHOP_ADDRESS.address}
                          </p>
                          <p className="card-text mb-1">
                            <strong>City:</strong> {SHOP_ADDRESS.city}
                          </p>
                          <p className="card-text mb-1">
                            <strong>Phone:</strong> {SHOP_ADDRESS.phone}
                          </p>
                          <p className="card-text mb-0">
                            <strong>Business Hours:</strong> {SHOP_ADDRESS.hours}
                          </p>
                        </div>
                      </div>
                      <div className="alert alert-info mt-3 mb-0">
                        <small>
                          <strong>💡 Pickup Instructions:</strong> Please bring your order confirmation and a valid ID when picking up your order.
                          We'll notify you when your order is ready for pickup.
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Selection */}
              <p className="mt-4 checkout_title">Payment Method</p>

              <div className="row">
                {/* Cash on Delivery */}
                <div className="col-6 mb-3">
                  <div
                    className={`payment-option d-flex flex-column align-items-center p-3 border rounded ${paymentMethod === "cash" ? "selected" : ""}`}
                    onClick={() => setPaymentMethod("cash")}
                  >
                    <FaMoneyBillWave size={40} className="mb-2 text-success" />
                    <span className="fw-semibold">
                      {deliveryMethod === "pickup" ? "Cash on Pickup" : "Cash on Delivery"}
                    </span>
                    <small className="text-muted text-center mt-1">
                      {deliveryMethod === "pickup" ? "Pay when you pickup" : "Pay when you receive"}
                    </small>
                  </div>
                </div>

                {/* Bank Transfer */}
                <div className="col-6 mb-3">
                  <div
                    className={`payment-option d-flex flex-column align-items-center p-3 border rounded ${paymentMethod === "bank_transfer" ? "selected" : ""} ${true ? "disabled" : ""}`}
                    onClick={() => !true && setPaymentMethod("bank_transfer")}
                    style={true ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                  >
                    <FaCreditCard size={40} className="mb-2 text-primary" />
                    <span className="fw-semibold">Bank Transferss</span>
                    <small className="text-muted text-center mt-1">Transfer money & upload slip</small>
                  </div>
                </div>
              </div>

              {/* Bank Transfer Details */}
              {paymentMethod === "bank_transfer" && (
                <div className="mt-3 p-3 border rounded bg-light">
                  <h6 className="mb-3">
                    <FaCreditCard className="me-2" />
                    Bank Transfer Details
                  </h6>

                  <div className="bank-details">
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <strong>Bank Name:</strong>
                        <p className="mb-1">{bankDetails.bank_name}</p>
                      </div>
                      <div className="col-md-6 mb-2">
                        <strong>Account Number:</strong>
                        <p className="mb-1 text-primary fw-bold">{bankDetails.account_number}</p>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <strong>Account Name:</strong>
                        <p className="mb-1">{bankDetails.account_name}</p>
                      </div>
                      <div className="col-md-6 mb-2">
                        <strong>Reference Email:</strong>
                        <p className="mb-1">{bankDetails.email}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="form-label fw-semibold">
                      <FaUpload className="me-2" />
                      Upload Payment Slip *
                    </label>
                    <input
                      type="file"
                      className={`form-control ${errors.slip ? 'is-invalid' : ''}`}
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleFileChange}
                    />
                    {errors.slip && <div className="invalid-feedback d-block">{errors.slip}</div>}

                    <small className="text-muted">
                      Upload a clear image or PDF of your bank transfer slip (Max: 5MB)
                    </small>

                    {/* Slip Preview */}
                    {slipPreview && (
                      <div className="mt-2">
                        <p className="mb-1 fw-semibold">Preview:</p>
                        {bankSlip.type.startsWith('image/') ? (
                          <div className="d-flex align-items-center">
                            <img
                              src={slipPreview}
                              alt="Payment slip preview"
                              className="img-thumbnail me-3"
                              style={{ maxWidth: '150px', maxHeight: '150px' }}
                            />
                            <div>
                              <FaCheckCircle className="text-success me-2" />
                              <small>File ready: {bankSlip.name}</small>
                            </div>
                          </div>
                        ) : (
                          <div className="border p-2 rounded d-flex align-items-center">
                            <FaCheckCircle className="text-success me-2" />
                            <div>
                              <strong>PDF File:</strong> {bankSlip.name}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="alert alert-info mt-3">
                    <strong>💡 Important:</strong> Please include your order ID in the transfer reference.
                    Your order will be processed once we verify the payment. You can also email the slip to {bankDetails.email}
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              <p className="mt-4 checkout_title">Additional Notes</p>
              <textarea
                className="form-control"
                rows="3"
                placeholder={
                  deliveryMethod === "pickup"
                    ? "Enter any special instructions, preferred pickup time, or additional information..."
                    : "Enter any special instructions, delivery notes, preferred delivery time, or additional information..."
                }
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
              ></textarea>
            </form>
          </div>

          {/* RIGHT COLUMN - Order Summary */}
          <div className="col-md-5">
            <div className="p-4" style={{ backgroundColor: "#f8f9fa", borderRadius: "10px", position: 'sticky', top: '20px' }}>
              <p className="summary_title">Order Summary</p>

              {/* Cart Items */}
              <div className="cart-items mb-3">
                {cart.items.map((item) => (
                  <div key={item.id} className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                    <div className="d-flex align-items-center">
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        style={{ width: "60px", height: "60px", objectFit: "cover" }}
                        className="me-3 rounded"
                      />
                      <div>
                        <p className="mb-1 product_order_title" style={{ fontSize: "14px" }}>
                          {item.productName}
                        </p>
                        {item.variantName && (
                          <p className="mb-0 text-muted" style={{ fontSize: "12px" }}>
                            Variant: {item.variantName}
                          </p>
                        )}
                        <p className="mb-0" style={{ fontSize: "13px" }}>Qty: {item.quantity}</p>
                      </div>
                    </div>
                    <span className="fw-semibold">Rs.{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <hr />

              {/* Delivery/Pickup Information */}
              <div className="delivery-info mb-3 p-3 bg-white rounded border">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold">Delivery Method</span>
                  <span className={`badge ${deliveryMethod === "delivery" ? "bg-primary" : "bg-success"}`}>
                    {deliveryMethod === "delivery" ? "Home Delivery" : "Store Pickup"}
                  </span>
                </div>

                {deliveryMethod === "delivery" && userLocation && (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold">Delivery Distance</span>
                      <span>{deliveryDistance.toFixed(2)} km</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-semibold">Delivery Fee</span>
                      <span className="fw-bold text-primary">Rs.{deliveryFee.toFixed(2)}</span>
                    </div>
                    <small className="text-muted d-block mt-1">
                      * Rs.100 base fee + Rs.50 per km from Moratuwa
                    </small>
                  </>
                )}

                {deliveryMethod === "pickup" && (
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Pickup Fee</span>
                    <span className="fw-bold text-success">FREE</span>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="price-breakdown">
                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal ({cart.items.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
                  <span>Rs.{subTotal.toFixed(2)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Tax (0%)</span>
                  <span>Rs.{tax.toFixed(2)}</span>
                </div>

                {deliveryMethod === "delivery" && userLocation && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Delivery Fee</span>
                    <span>Rs.{deliveryFee.toFixed(2)}</span>
                  </div>
                )}

                {deliveryMethod === "pickup" && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>Pickup Fee</span>
                    <span className="text-success">FREE</span>
                  </div>
                )}

                <hr />
                <div className="total-text d-flex justify-content-between mb-4 fw-bold fs-5">
                  <span>Total Amount</span>
                  <span className="text-primary">Rs.{total.toFixed(2)}</span>
                </div>
              </div>

              {deliveryMethod === "delivery" && !userLocation && (
                <div className="alert alert-warning">
                  <small>
                    <FaMapMarkerAlt className="me-1" />
                    Select your location to calculate delivery fee
                  </small>
                </div>
              )}

              <button
                className="btn w-100 py-3 fw-bold"
                style={{
                  backgroundColor: "#FD7E14",
                  color: "#fff",
                  border: "none",
                  fontSize: "1.1rem",
                  borderRadius: "8px"
                }}
                onClick={handlePlaceOrder}
                disabled={loading || (deliveryMethod === "delivery" && !userLocation)}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing Order...
                  </>
                ) : (
                  `Place Order - Rs.${total.toFixed(2)}`
                )}
              </button>

              {paymentMethod === "cash" && deliveryMethod === "delivery" && (
                <div className="alert alert-warning mt-3 mb-0">
                  <small>
                    <strong>💰 Cash on Delivery:</strong> Please have exact change ready.
                    Delivery charges are calculated based on distance from our Moratuwa store.
                  </small>
                </div>
              )}

              {paymentMethod === "cash" && deliveryMethod === "pickup" && (
                <div className="alert alert-warning mt-3 mb-0">
                  <small>
                    <strong>💰 Cash on Pickup:</strong> Please have exact change ready when picking up your order from our store.
                  </small>
                </div>
              )}

              {paymentMethod === "bank_transfer" && (
                <div className="alert alert-info mt-3 mb-0">
                  <small>
                    <strong>🏦 Bank Transfer:</strong> Your order will be confirmed after payment verification.
                    Please upload the payment slip or email it to us.
                  </small>
                </div>
              )}

              {/* Security Notice */}
              <div className="text-center mt-3">
                <small className="text-muted">
                  🔒 Your information is secure and encrypted
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Checkout;